import { Stack } from "PFComponents/containers";
import { Modal } from "PFComponents/modal";
import { MultiToggle, Option } from "PFComponents/multi_toggle";
import { NotificationFrequency, NotificationSetting } from "PFTypes";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

type BulkEditModalProps = {
  selectedSettings: NotificationSetting[];
  onClose: VoidFunction;
  onApply: VoidFunction;
  withImportantToggle?: boolean;
};

export const BulkEditModal = ({
  selectedSettings,
  withImportantToggle,
  onClose,
  onApply
}: BulkEditModalProps) => {
  const { t } = useTranslation("notificationsSettings");
  const { t: tCore } = useTranslation();
  const { setValue } = useFormContext();

  const [selectedFrequency, setSelectedFrequency] = useState<NotificationFrequency | null>(null);
  const [selectedImportant, setSelectedImportant] = useState<boolean | null>(null);

  const frequencyOptions: Option<NotificationFrequency>[] = selectedSettings[0].frequencyOptions.map(
    (frequency) => ({
      id: frequency,
      value: t(`frequency.${frequency}`)
    })
  );

  const importantOptions: Option<number>[] = [
    { id: 1, value: tCore("yes") },
    { id: 0, value: tCore("no") }
  ];

  const handleApply = () => {
    selectedSettings.forEach(({ id }) => {
      if (selectedFrequency) {
        setValue(`${id}.frequency`, selectedFrequency, { shouldDirty: true });
      }
      if (selectedImportant !== null) {
        setValue(`${id}.important`, selectedImportant, { shouldDirty: true });
      }
    });
    onApply();
  };

  return (
    <Modal
      title={t("bulkEdit.title")}
      subtitle={t("bulkEdit.subtitle", { count: selectedSettings.length })}
      onClose={onClose}
      onOK={handleApply}
      onCancel={onClose}
      labelOK={tCore("apply")}
      disableOKButton={!selectedFrequency && selectedImportant === null}
    >
      <Stack gap="spacingLg">
        <MultiToggle<NotificationFrequency>
          label={t("frequency.label")}
          options={frequencyOptions}
          controlledValue={selectedFrequency}
          onChange={setSelectedFrequency}
        />
        {withImportantToggle && (
          <MultiToggle<number>
            label={t("important.label")}
            options={importantOptions}
            controlledValue={selectedImportant !== null ? (selectedImportant ? 1 : 0) : null}
            onChange={(value) => setSelectedImportant(value === 1)}
          />
        )}
      </Stack>
    </Modal>
  );
};
